// src/page/PanelComercio.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
  collection, query, where, getDocs,
  doc, updateDoc, Timestamp, getDoc,
  deleteDoc, serverTimestamp, addDoc, arrayUnion
} from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { db } from "../firebase/config";
import logo from '../asset/logoSumaSalto.png';
import Footer from "../asset/components/Footer";

const PanelComercio = () => {
  const [ci, setCi] = useState("");
  const [cliente, setCliente] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    ci: "",
    puntos: 0,
    comercioOrigen: "",
  });
  const [error, setError] = useState("");

  // Notificaciones
  const [mensaje, setMensaje] = useState('');
  const [titulo, setTitulo] = useState('');
  const [mensajeExito, setMensajeExito] = useState('');

  // 👉 NUEVO: estado para config global y comercio actual
  const [config, setConfig] = useState(null); // { ValorCanjeGlobal, Vueltas, MontoMaximo, TopeTipo }
  const [comercioActual, setComercioActual] = useState(null); // nombre del comercio

  // 👉 NUEVO: calculadora
  const [puntosACanjear, setPuntosACanjear] = useState("");

  // Cargar config y comercio actual al montar
  useEffect(() => {
    const cargarConfigYComercio = async () => {
      try {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        // comercio actual (nombre por UID)
        const comercioRef = doc(db, "comercios", uid);
        const comercioSnap = await getDoc(comercioRef);
        if (comercioSnap.exists()) {
          setComercioActual(comercioSnap.data().nombre || "Comercio desconocido");
        }

        // config global
        const cfgRef = doc(db, "config", "global");
        const cfgSnap = await getDoc(cfgRef);
        if (cfgSnap.exists()) {
          setConfig(cfgSnap.data());
        }
      } catch (e) {
        console.error("Error cargando config/comercio:", e);
      }
    };
    cargarConfigYComercio();
  }, []);

  // 🔎 Buscar cliente por CI
  const buscarCliente = async () => {
    try {
      const coleccion = collection(db, "clientes");
      const valor = ci.trim();

      if (!valor) {
        setError("Debe ingresar un número de CI");
        return;
      }

      const q = query(coleccion, where("ci", "==", valor));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError("Cliente no encontrado");
        setCliente(null);
        setFormData({
          nombre: "",
          email: "",
          ci: valor,
          puntos: 0,
          comercioOrigen: "",
        });
      } else {
        querySnapshot.forEach((docSnap) => {
          const data = { id: docSnap.id, ...docSnap.data() };
          setCliente(data);
          setFormData({
            nombre: data.nombre || "",
            email: data.email || "",
            ci: data.ci || valor,
            puntos: data.puntos || 0,
            comercioOrigen: data.comercioOrigen || "",
          });
          setError("");
        });
      }
    } catch (e) {
      console.error("Error en la búsqueda:", e);
      setError("Ocurrió un error al buscar el cliente");
    }
  };

  // 🧮 Valor del canje a aplicar HOY (derivado)
  const estadoCanje = useMemo(() => {
    if (!cliente || !comercioActual || !config) return null;
    return calcularEstadoCanje(cliente, comercioActual, config);
  }, [cliente, comercioActual, config]);

  const valorCanjeMostrar = estadoCanje?.valorAplicable ?? 0;

  // 💸 Monto a descontar según la calculadora
  const montoCalculado = useMemo(() => {
    const pts = Number(puntosACanjear) || 0;
    const valor = Number(valorCanjeMostrar) || 0;
    let dinero = pts * valor;

    if (!config) return { puntosOk: pts, dineroOk: dinero };

    const topeTipo = (config.TopeTipo || "").toLowerCase();
    const max = Number(config.MontoMaximo) || 0;

    if (topeTipo === "dinero" && max > 0) {
      if (dinero > max) {
        const puntosMaxPorDinero = Math.floor(max / (valor || 1));
        return { puntosOk: puntosMaxPorDinero, dineroOk: puntosMaxPorDinero * valor };
      }
    } else if (topeTipo === "puntos" && max > 0) {
      const ptsOk = Math.min(pts, max);
      return { puntosOk: ptsOk, dineroOk: ptsOk * valor };
    }

    return { puntosOk: pts, dineroOk: dinero };
  }, [puntosACanjear, valorCanjeMostrar, config]);

  // 🔁 Canjear puntos
  const canjearPuntos = async () => {
    if (!cliente) {
      setError("Debe buscar y seleccionar un cliente primero");
      return;
    }
    if (!estadoCanje) {
      setError("Aún no se pudo calcular el valor del canje.");
      return;
    }

    const puntosIngresados = parseInt(prompt("Ingrese la cantidad de puntos a canjear:"), 10);
    if (isNaN(puntosIngresados) || puntosIngresados <= 0) {
      setError("Ingrese un número válido de puntos");
      return;
    }

    if (puntosIngresados > (cliente.puntos || 0)) {
      setError("No puede canjear más puntos de los que el cliente posee");
      return;
    }

    // Respetar tope (dinero o puntos)
    let puntosAUsar = puntosIngresados;
    const valor = estadoCanje.valorAplicable;

    if (config) {
      const topeTipo = (config.TopeTipo || "").toLowerCase();
      const max = Number(config.MontoMaximo) || 0;
      if (topeTipo === "dinero" && max > 0) {
        const dinero = puntosAUsar * valor;
        if (dinero > max) {
          puntosAUsar = Math.floor(max / (valor || 1));
        }
      } else if (topeTipo === "puntos" && max > 0) {
        puntosAUsar = Math.min(puntosAUsar, max);
      }
    }

    if (puntosAUsar <= 0) {
      setError("Con el tope configurado, no se pueden canjear puntos.");
      return;
    }

    try {
      const nuevoSaldo = (cliente.puntos || 0) - puntosAUsar;
      const clienteRef = doc(db, "clientes", cliente.id);

      // 1) Actualizar puntos
      await updateDoc(clienteRef, { puntos: nuevoSaldo });

      // 2) Actualizar ultimoCanje + historial, guardando el PRÓXIMO estado
      await actualizarCanjesExtendido(cliente.id, estadoCanje, comercioActual, cliente.historialCanjes);

      // 3) Refrescar estado local
      setCliente((prev) => ({ ...prev, puntos: nuevoSaldo }));
      setFormData((prev) => ({ ...prev, puntos: nuevoSaldo }));

      setError("");
      alert(`Canje realizado.
Puntos usados: ${puntosAUsar}
Valor por punto: $${valor}
Descuento aplicado: $${puntosAUsar * valor}
Nuevo saldo: ${nuevoSaldo} puntos.`);
    } catch (error) {
      console.error("Error al canjear puntos:", error);
      setError("Ocurrió un error al canjear puntos");
    }
  };

  // 🔄 Actualizar ultimoCanje / historial con el PRÓXIMO estado
  const actualizarCanjesExtendido = async (clienteId, estado, nombreComercioActual, historialActual = []) => {
    try {
      const clienteRef = doc(db, "clientes", clienteId);

      // Ultimo canje con el PRÓXIMO estado para este comercio
      const nuevoUltimo = {
        fecha: Timestamp.now(),
        comercio: nombreComercioActual,
        valorCanjeActual: estado.valorNext,
        direccion: estado.direccionNext,
      };

      // Entrada detallada para historialCanjes
      const historialEntry = {
        comercio: nombreComercioActual,
        valorAplicado: estado.valorAplicable, // valor usado hoy
        valorNext: estado.valorNext,
        direccionNext: estado.direccionNext,
        valorCanjeVuelta: { [nombreComercioActual]: estado.valorNext },
        direccionCanjeVuelta: { [nombreComercioActual]: estado.direccionNext },
        fecha: Timestamp.now(),
      };

      await updateDoc(clienteRef, {
        // mapas por comercio para retomar ciclo cuando vuelva a este comercio
        [`valorCanjeVuelta.${nombreComercioActual}`]: estado.valorNext,
        [`direccionCanjeVuelta.${nombreComercioActual}`]: estado.direccionNext,

        // campos "legacy" de conveniencia
        ultimoCanje: nuevoUltimo,
        valorCanjeActual: estado.valorNext,
        direccionCanje: estado.direccionNext,

        // log de historial (no sobreescribe, agrega una entrada)
        historialCanjes: arrayUnion(historialEntry),
      });
    } catch (error) {
      console.error("Error al actualizar canjes extendido:", error);
    }
  };

   
  // Logout
  const handleLogout = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      console.log("✅ Sesión cerrada correctamente");
      window.location.href = "/panel-comercio";
    } catch (error) {
      console.error("❌ Error al cerrar sesión:", error.message);
    }
  };

  // Notificaciones (tu bloque intacto)
  const enviarNotificacion = async () => {
    try {
      if (!mensaje.trim() || !titulo.trim()) {
        alert('Debes completar título y mensaje.');
        return;
      }
      const auth = getAuth();
      const uidComercio = auth.currentUser?.uid;
      if (!uidComercio) {
        console.error('❌ No hay comercio logueado.');
        return;
      }
      const comercioRef = doc(db, "comercios", uidComercio);
      const comercioSnap = await getDoc(comercioRef);
      if (!comercioSnap.exists()) {
        console.error('❌ No se encontró el comercio en Firestore.');
        return;
      }
      const nombreComercio = comercioSnap.data().nombre || "Comercio desconocido";

      const q = query(collection(db, 'notificaciones'), where('uidComercio', '==', uidComercio));
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        await deleteDoc(docSnap.ref);
        console.log(`🗑 Notificación anterior eliminada para ${nombreComercio}`);
      }

      await addDoc(collection(db, 'notificaciones'), {
        uidComercio,
        nombreComercio,
        titulo,
        mensaje,
        fecha: serverTimestamp(),
        aprobado: false,
        promocion: 'aviso'
      });

      console.log(`✅ Notificación enviada para aprobación: ${nombreComercio}`);
      setMensaje('');
      setTitulo('');
      setMensajeExito('✅ Notificación enviada y pendiente de aprobación');
      setTimeout(() => setMensajeExito(''), 4000);
    } catch (error) {
      console.error('❌ Error enviando la notificación:', error);
    }
  };

  return (
    <div style={{ padding: "20px" }}>
      <img src={logo} alt="logoSumaSalto.png" style={{ height: '100px', width: 'auto' }} />
      <h1>Panel Comercio</h1>

      <h3 className="text-lg font-semibold mt-6">Búsqueda por CI</h3>
      <div style={{ marginBottom: "20px" }}>
        <input
          type="text"
          value={ci}
          onChange={(e) => setCi(e.target.value)}
          placeholder="Ingrese CI del cliente"
          style={{ padding: "5px", marginRight: "10px" }}
        />
        <button onClick={buscarCliente}>Buscar</button>
      </div>

      {error && <p style={{ color: "red" }}>{error}</p>}

      {cliente && (
        <div style={{ border: "1px solid #ccc", padding: "10px", marginBottom: 12 }}>
          <h3>Datos del Cliente</h3>
          <p><strong>Nombre:</strong> {formData.nombre}</p>
          <p><strong>Email:</strong> {formData.email}</p>
          <p><strong>CI:</strong> {formData.ci}</p>
          <p><strong>Puntos:</strong> {formData.puntos}</p>
          <p><strong>Comercio Origen:</strong> {formData.comercioOrigen}</p>
          <p><strong>Comercio Actual:</strong> {comercioActual || '...'}</p>
          <p><strong>Valor del punto en este comercio (hoy):</strong> ${valorCanjeMostrar}</p>
          {config && (
            <p style={{ opacity: 0.8 }}>
              <em>Reglas: tope = ${Number(config.ValorCanjeGlobal) || 0}, vueltas = {Number(config.Vueltas) || 0}</em>
            </p>
          )}
        </div>
      )}

      {/* Calculadora simple */}
      {cliente && (
        <div style={{ border: "1px solid #ddd", padding: 10, borderRadius: 6, marginBottom: 12 }}>
          <h3>Calculadora de canje</h3>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <input
              type="number"
              min="0"
              value={puntosACanjear}
              onChange={(e) => setPuntosACanjear(e.target.value)}
              placeholder="Puntos a canjear"
              style={{ padding: "6px" }}
            />
            <span>=</span>
            <strong>${montoCalculado.dineroOk || 0}</strong>
            <span style={{ opacity: 0.7 }}>
              (usando {montoCalculado.puntosOk} pts × ${valorCanjeMostrar})
            </span>
          </div>
        </div>
      )}

      {cliente && (
        <div style={{ marginTop: "12px" }}>
          <button onClick={canjearPuntos}>Canjear puntos</button>
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <h3>Enviar notificación para aprobación</h3>
        <input
          type="text"
          placeholder="Título"
          value={titulo}
          maxLength={50}
          onChange={(e) => setTitulo(e.target.value)}
          style={{ display: 'block', marginBottom: '0.5rem', width: '100%' }}
        />
        <textarea
          placeholder="Escribe tu mensaje..."
          value={mensaje}
          maxLength={150}
          onChange={(e) => setMensaje(e.target.value)}
          style={{ display: 'block', marginBottom: '0.5rem', width: '100%', height: '80px' }}
        />
        <button onClick={enviarNotificacion}>Enviar para aprobación</button>

        {mensajeExito && (
          <div style={{ color: 'green', marginTop: '10px', fontWeight: 'bold' }}>
            {mensajeExito}
          </div>
        )}
      </div>

      <button
        onClick={handleLogout}
        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
        style={{ marginTop: 16 }}
      >
        Cerrar Sesión
      </button>

      <div className="flex flex-col min-h-screen">
        <main className="flex-grow"></main>
        <Footer />
      </div>
    </div>
  );
}; 

// ============ LÓGICA DE CÁLCULO ============
// Devuelve el valor a aplicar hoy y el próximo estado para guardar
function calcularEstadoCanje(cliente, nombreComercioActual, config) {
  const tope = Number(config?.ValorCanjeGlobal) || 3;
  const vueltas = Number(config?.Vueltas) || 3;
  const min = 1;

  // step: cuántas unidades mueve por visita (según fórmula nueva)
  const step = vueltas > 0 ? tope / vueltas : tope;

  const clamp = (x) => Math.min(tope, Math.max(min, x));

  const { ultimoCanje, comercioOrigen } = cliente || {};

  // NUEVO: si ya hay estado guardado por comercio, retomamos desde ahí
  const mapValores = cliente?.valorCanjeVuelta || {};
  const mapDirs = cliente?.direccionCanjeVuelta || {};
  const tienePerComercio = mapValores[nombreComercioActual] != null;
  if (tienePerComercio) {
    const prevValor = Number(mapValores[nombreComercioActual]);
    const prevDir = (mapDirs[nombreComercioActual] || "up");
    const valorAplicable = clamp(prevValor);

    let valorNext = prevValor;
    let dirNext = prevDir;
    if (prevDir === "up") {
      valorNext = clamp(prevValor + step);
      if (valorNext >= tope) dirNext = "down";
    } else {
      valorNext = clamp(prevValor - step);
      if (valorNext <= min) dirNext = "up";
    }

    return {
      valorAplicable,
      direccionActual: prevDir,
      valorNext,
      direccionNext: dirNext,
      step, tope, min,
    };
  }

  const tieneUltimo = !!(ultimoCanje && ultimoCanje.comercio);

  // ► Si NO tiene ultimoCanje o cambia de comercio, arranque con base segun origen
  if (!tieneUltimo || ultimoCanje.comercio !== nombreComercioActual) {
    const esOrigen = nombreComercioActual === comercioOrigen;
    const base = esOrigen ? 3 : 1;
    const dirActual = esOrigen ? "down" : "up"; // en origen arrancamos bajando
    const valorAplicable = clamp(base);

    // calcular siguiente estado
    let valorNext = base;
    let dirNext = dirActual;
    if (dirActual === "up") {
      valorNext = clamp(base + step);
      if (valorNext >= tope) dirNext = "down";
    } else {
      valorNext = clamp(base - step);
      if (valorNext <= min) dirNext = "up";
    }

    return {
      valorAplicable,
      direccionActual: dirActual,
      valorNext,
      direccionNext: dirNext,
      step, tope, min,
    };
  }

  // ► Mismo comercio que el último → continuar ciclo desde ultimoCanje
  const prevValor = typeof ultimoCanje.valorCanjeActual === "number"
    ? ultimoCanje.valorCanjeActual
    : (nombreComercioActual === comercioOrigen ? 3 : 1);
  const prevDir = ultimoCanje.direccion || (nombreComercioActual === comercioOrigen ? "down" : "up");

  const valorAplicable = clamp(prevValor);

  let valorNext = prevValor;
  let dirNext = prevDir;

  if (prevDir === "up") {
    valorNext = clamp(prevValor + step);
    if (valorNext >= tope) dirNext = "down";
  } else {
    valorNext = clamp(prevValor - step);
    if (valorNext <= min) dirNext = "up";
  }

  return {
    valorAplicable,
    direccionActual: prevDir,
    valorNext,
    direccionNext: dirNext,
    step, tope, min,
  };
}


export default PanelComercio;
